import {
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { conversationChannelAttributes } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import { useSetState } from 'react-use';

import { bansApi } from 'hooks/useBans/api';
import { UnbanUserResponse } from 'hooks/useBans/types';
import { useNotifications } from 'hooks/useNotifications';

const { unbanUser } = bansApi;

/** The interface of the event that is dispatched when the modal is closed. */
export interface UnbanUserModalOnCloseEvent {
  /** Whether or not the modal was canceled. */
  canceled?: boolean;
  /** The response payload from a successful unban. */
  response?: UnbanUserResponse;
}

interface UnbanUserModalProps {
  user: User | User;
  channel: Channel;
  isOpen: boolean;
  onClose: (event: UnbanUserModalOnCloseEvent) => void;
}

interface UnbanUserModalState {
  reason?: string;
  isBanning?: boolean;
}

export const UnbanUserModal = (props: UnbanUserModalProps): JSX.Element => {
  const [state, setState] = useSetState<UnbanUserModalState>({
    isBanning: false,
  });

  const notify = useNotifications();

  const handleClose = (event: UnbanUserModalOnCloseEvent) => {
    props.onClose(event);
  };

  const handleConfirm = async () => {
    const {
      user: { id },
      channel,
    } = props;
    const { reason } = state;

    ampli.bansUnbanUserClicked({
      channel,
    });

    setState({ isBanning: true });

    try {
      const response = await unbanUser(id, {
        channel,
        reason,
      });

      const notificationPayload = {
        title: 'User unbanned',
        text: `User has been unbanned`,
        icon: 'i-done',
        className: 'success',
        time: 5000,
      };

      notify(notificationPayload);
      setState({ isBanning: false });
      handleClose({ response });
    } catch (error) {
      setState({ isBanning: false });
      const notificationPayload = {
        title: 'An error occurred',
        text: `An error occurred when trying to unban user`,
        icon: 'i-exclamation',
        className: 'danger',
      };

      notify(notificationPayload);
      throw new Error('Failed to unban user');
    }
  };

  const handleCancel = () => {
    handleClose({ canceled: true });
  };

  const {
    isOpen,
    user: { name },
    channel,
  } = props;
  const channelName = conversationChannelAttributes.get(channel)?.prettyName;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel}>
      <ModalHead title="Are you sure?" onClose={handleCancel} />
      <ModalBody>
        <Text.Paragraph>
          Are you sure you want to unban user <strong>{name}</strong> from the{' '}
          <strong>{channelName}</strong> channel?
        </Text.Paragraph>
      </ModalBody>
      <ModalFoot
        cancelName="Cancel"
        cancelOnClick={handleCancel}
        confirmName="Unban user"
        confirmProps={{
          isLoading: state.isBanning,
          loadingIndicator: (
            <Text.Paragraph as="span" variant="3" wrap="nowrap" color="inherit">
              Unbanning...
            </Text.Paragraph>
          ),
        }}
        confirmOnClick={handleConfirm}
      ></ModalFoot>
    </Modal>
  );
};
